import { get } from "lodash";
import moment from "moment";
import format from "string-template";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import { validateBIC, validateIBAN, validateNIR } from "utils/helpers/values";
import * as yup from "yup";

const validation = (
  messages,
  { hasRia, hasPartner, isRenaultSalary, isTns },
) => {
  const string = yup.string().typeError(messages.REQUIRED).nullable();
  const nameValidation = yup
    .string()
    .typeError(messages.NOT_STRING)
    .nullable()
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );
  const requiredString = yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED);

  const relatedPersonValidationSchema = ({ birthDate }) =>
    yup.object().shape({
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation,
          firstname: nameValidation,
          maritalName: nameValidation,
          birthDate,
        }),
        socioProfessionalCategory: string,
        socialRegime: string,
        socialSecurityNumber: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .notRequired()
          // .required(messages.REQUIRED)
          .test(
            "validateNIR",
            messages.INVALID_SOCIAL_SECURITY_NUMBER,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);

              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        telecoms: yup.object().shape({
          [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
            value: string,
          }),
          [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
            value: string,
          }),
        }),
      }),
    });
  return yup.object().shape({
    createdDate: yup.date(),
    isPayerReceiver: yup.string().typeError(messages.NOT_STRING),
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
        referralCode: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .notRequired()
          .test(
            "isValid",
            format(messages.INVALID_REFERRAL_CODE, { length: 6 }),
            function (value) {
              return value
                ? new RegExp(REGEX_EXPS.referralCode).test(
                    value.replace(/\s+/g, ""),
                  )
                : true;
            },
          ),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date(),
        caseType: string,
        saleMode: string,
      }),
      terminationRequest:
        hasRia &&
        yup.object().shape({
          address: string,
          contractNumber: string,
          brokerageMandate: yup.object().shape({
            lastName: string,
            firstName: string,
            currentCompany: string,
          }),
        }),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          maritalName: nameValidation,
          birthDate: yup.date(),
        }),
        socialRegime: string,
        siretNumber: isTns && string,
        socialSecurityNumber: yup
          .string()
          .typeError(messages.REQUIRED)
          .nullable()
          .notRequired()
          // .required(messages.REQUIRED)
          .test(
            "validateNIR",
            messages.INVALID_SOCIAL_SECURITY_NUMBER,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        socioProfessionalCategory: string,
        politicallyExposedPerson: yup.boolean().nullable(),
        fields: yup.object().shape({
          affiliateOrganizationCode: yup
            .string()
            .typeError(messages.NOT_STRING)
            .nullable(),
          matriculation: isRenaultSalary && string,
          socialReason: isTns && string,
          nafCode: isTns && string,
          organizationNumber: isTns && string,
          requestLafpacMemberShip: yup.boolean().nullable(),
          alreadyLafpacMember: yup.boolean().nullable(),
          lafpacMemberShipNumber: string,
          familySituation: string,
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: yup.object().shape({
        [CONTACT_POINT_SYSTEM.STREET]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.RESIDENCE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
          value: yup
            .string()
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, function (value) {
              return value
                ? new RegExp(REGEX_EXPS.internationalMobilePhone).test(
                    value.replace(/\s+/g, ""),
                  )
                : true;
            }),
          // .test(
          //   "phoneConflict",
          //   messages.PHONE_CONFLICT,
          //   async function (value) {
          //     const isValidRegex = new RegExp(REGEX_EXPS.phone).test(value);
          //     if (!value || !isValidRegex) {
          //       return true;
          //     }
          // const {
          //   data: { user },
          // } = await checkUser({
          //   variables: {
          //     where: { phone: value },
          //   },
          // });
          // return !user?.id;
          // }
          // ),
        }),
        [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
          value: yup
            .string()
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
            }),
        }),
        [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
          value: yup.string().nullable().email(messages.EMAIL),
          // .test(
          //   "emailConflict",
          //   messages.EMAIL_CONFLICT,
          //   async function (value) {
          //     if (!value || !yup.string().email().isValidSync(value)) {
          //       return true;
          //     }
          //     const {
          //       data: { user },
          //     } = await checkUser({
          //       variables: {
          //         where: { email: value },
          //       },
          //     });
          //     return !user?.id;
          //   }
          // ),
        }),
      }),
      payer: yup.object().shape({
        [CONTACT_POINT_SYSTEM.STREET]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
      }),
    }),
    spouse:
      hasPartner &&
      relatedPersonValidationSchema({
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
          .min(
            moment().subtract(100, "year").startOf("year"),
            messages.RADIANCE.SPOUSE_AGE,
          ),
        // .required(messages.REQUIRED),
      }),
    children: yup.array().of(
      yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            lastname: nameValidation,
            firstname: nameValidation,
            birthDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .max(moment(), messages.MIN_TODAY),
            // .required(messages.REQUIRED),
          }),
          socioProfessionalCategory: string,
          socialRegime: string,
          fields: yup.object().shape({
            refundOtherAccount: yup.boolean(),
            payment: yup.object().shape({
              IBAN: yup
                .string()
                .nullable()
                .when("..refundOtherAccount", {
                  is: true,
                  then: (schema) =>
                    schema
                      .typeError(messages.REQUIRED)
                      .nullable()
                      .notRequired()
                      .test(
                        "validateIBAN",
                        messages.INVALID_IBAN,
                        function (value, ctx) {
                          if (!value) return true;
                          const result = validateIBAN(value);
                          if (!result.valid) {
                            return ctx.createError({
                              message: get(
                                messages,
                                result.reason,
                                messages.INVALID_VALUE,
                              ),
                            });
                          }
                          return true;
                        },
                      ),
                  otherwise: (schema) => schema.strip(),
                }),
              BIC: yup
                .string()
                .typeError(messages.NOT_STRING)
                .nullable()
                .test(
                  "validateBIC",
                  messages.INVALID_BIC || messages.INVALID_IBAN,
                  function (value) {
                    if (!value) return true;
                    const result = validateBIC(value);
                    return result.valid;
                  },
                ),
            }),
          }),
        }),
      }),
    ),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      payer: yup.object().shape({
        BIC: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: yup
          .string()
          .typeError(messages.REQUIRED)
          .nullable()
          .notRequired()
          .test("validateIBAN", messages.INVALID_IBAN, function (value, ctx) {
            if (!value) return true;
            const result = validateIBAN(value);
            if (!result.valid)
              return ctx.createError({
                message: get(messages, result.reason, messages.INVALID_VALUE),
              });

            return true;
          }),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          type: string,
          socialReason: string,
          gender: string,
          firstName: string,
          lastName: string,
        }),
      }),
      receiver: yup.object().shape({
        BIC: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: yup
          .string()
          .typeError(messages.REQUIRED)
          .nullable()
          .notRequired()
          .test("validateIBAN", messages.INVALID_IBAN, function (value, ctx) {
            if (!value) return true;
            const result = validateIBAN(value);
            if (!result.valid)
              return ctx.createError({
                message: get(messages, result.reason, messages.INVALID_VALUE),
              });

            return true;
          }),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
